import { Link } from "react-router-dom";
import { useRef, useEffect, useState } from "react";
import iconEjercicio from "@/assets/icon-ejercicio.webp";
import iconFotos from "@/assets/icon-fotos.webp";
import iconHospedaje from "@/assets/icon-hospedaje.webp";
import iconPaseo from "@/assets/icon-paseo.webp";
import iconVisita from "@/assets/icon-visita.webp";
import iconGuarderia from "@/assets/icon-guarderia.webp";
import iconCuidadoCasa from "@/assets/icon-cuidado-casa.webp";
import { serviceFilterOptions, serviceLabels, serviceEmojis } from "@/data/serviceLabels";

type ServiceCard = {
  label: string;
  desc: string;
  slug: string;
  icon?: string;
  emoji?: string;
};

const descriptions: Record<string, string> = {
  hospedaje_noche: "Tu mascota se queda en casa del cuidador",
  visita_domicilio: "El cuidador se queda en tu hogar",
  paseo_1hr: "El cuidador va a tu casa por visitas cortas",
  daycare: "Cuidado diurno mientras trabajás o salís",
  paseo_30min: "Paseos diarios con cuidadores de confianza",
  entrenamiento: "Actividad física guiada para tu mascota",
  sesion_fotos: "Recuerdos profesionales de tu mascota",
};

const iconsByKey: Record<string, string> = {
  entrenamiento: iconEjercicio,
  sesion_fotos: iconFotos,
  hospedaje_noche: iconHospedaje,
  visita_domicilio: iconCuidadoCasa,
  paseo_1hr: iconVisita,
  daycare: iconGuarderia,
  paseo_30min: iconPaseo,
};

const services: ServiceCard[] = serviceFilterOptions.map((s) => ({
  label: serviceLabels[s.value],
  desc: descriptions[s.value] || "",
  slug: s.slug,
  icon: iconsByKey[s.value],
  emoji: iconsByKey[s.value] ? undefined : serviceEmojis[s.value],
}));

const topRow = services.slice(0, 4);
const bottomRow = services.slice(4);

const Features = () => {
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.15 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const renderItem = (s: ServiceCard, i: number) => (
    <Link
      key={i}
      to={`/servicios/${s.slug}`}
      className={`group text-center px-4 py-6 rounded-2xl transition-all duration-500 cursor-pointer ${
        visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
      }`}
      style={{ transitionDelay: visible ? `${i * 80}ms` : "0ms" }}
    >
      {s.icon ? (
        <img
          src={s.icon}
          alt={s.label}
          width={120}
          height={120}
          loading="lazy"
          className="w-[100px] h-[100px] md:w-[120px] md:h-[120px] mx-auto mb-3 object-contain transition-transform duration-300 group-hover:scale-110"
        />
      ) : (
        <div className="w-[100px] h-[100px] md:w-[120px] md:h-[120px] mx-auto mb-3 flex items-center justify-center text-6xl md:text-7xl transition-transform duration-300 group-hover:scale-110">
          {s.emoji}
        </div>
      )}
      <h3 className="font-fredoka font-semibold text-foreground mb-1 text-lg md:text-xl transition-colors duration-300 group-hover:text-primary">
        {s.label}
      </h3>
      <p className="font-poppins text-muted-foreground leading-snug text-sm max-w-[180px] mx-auto">
        {s.desc}
      </p>
    </Link>
  );

  return (
    <section ref={sectionRef} id="servicios" className="section-padding" style={{ backgroundColor: "#fdf8e3" }}>
      <div className="container-custom mx-auto">
        <div
          className={`text-center mb-10 md:mb-14 transition-all duration-700 ${
            visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"
          }`}
        >
          <h2 className="text-3xl font-fredoka font-bold text-foreground mb-3 md:text-6xl">
            ¿Qué necesita tu mascota?
          </h2>
          <p className="text-base text-muted-foreground max-w-2xl mx-auto md:text-2xl">
            Encuentra el servicio ideal para tu mascota.
          </p>
        </div>

        {/* Mobile: 2-col grid / Desktop: staggered 4+3 rows */}
        <div className="hidden md:block">
          <div className="flex justify-center gap-8 lg:gap-12 mb-2">
            {topRow.map((s, i) => renderItem(s, i))}
          </div>
          <div className="flex justify-center gap-8 lg:gap-12">
            {bottomRow.map((s, i) => renderItem(s, i + topRow.length))}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-4 md:hidden">
          {services.map((s, i) => renderItem(s, i))}
        </div>
      </div>
    </section>
  );
};

export default Features;
